<template>
  <f7-page class="frequent-sales-orders-packaging">
    <template #fixed>
      <TheNavbar
        :nav-title="$t('commercialization.common.commonCombinations')"
        class="secondary-navbar"
        popup-close-btn-enable
      />
    </template>

    <UCPreload
      :show="isLoadingPackaging"
      :number="2"
    />

    <div
      class="container-search"
    >
      <div class="container">
        <input
          ref="inputSearchRef"
          class="container__filter-input margin-right"
          type="text"
          :placeholder="t('common.searchEllipsis')"
          clear-button="true"
          @input="handleSearchInput"
        >

        <div class="container__img">
          <div>
            <f7-icon
              v-if="textFilter === ''"
              f7="search"
              size="30px"
              color="grey"
            />
          </div>
          <div @click="changeSearch('')">
            <f7-icon
              v-if="textFilter !== ''"
              f7="arrow_left"
              size="30px"
              color="grey"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="content-accordion content-accordeon-component"
    >
      <div
        v-for="(option, j) in commonPackagingFilter"
        :key="j"
      >
        <UCAcordeon
          :items="[option]"
          title-option="Estado"
          :show-badge="false"
          :opened-index="0"
          primary=" var(--his-light-blue)"
          font-color="var(--his-slate-blue)"
          :after-title="false"
        >
          <template #subOption="{ item }">
            <f7-list media-list>
              <f7-list-item
                class="container-info"
                @click="emitSelectedPackaging(item)"
              >
                <div class="container-info__text">
                  <div class="title">
                    {{ `${t('common.product')}:` }}
                  </div>
                  <span class="info">{{ item.Producto?.Nombre }}</span>
                </div>
                <div class="container-info__text">
                  <div class="title">
                    {{ `${t('common.package')}:` }}
                  </div>
                  <span class="info">{{ item.Envase?.Nombre }}</span>
                </div>
                <div class="container-info__text">
                  <div class="title">
                    {{ `${t('common.pallet')}:` }}
                  </div>
                  <span class="info"> {{ item.Palet?.Nombre }}</span>
                </div>
                <div class="container-info__text">
                  <div class="title">
                    {{ `${t('common.brand')}:` }}
                  </div>
                  <span class="info"> {{ item.Marca?.Nombre }}</span>
                </div>
                <div class="container-info__text">
                  <div class="title">
                    {{ `${$t('commercialization.common.packagesPerPallet')}:` }}
                  </div>
                  <span class="info"> {{ item.EnvasesPorPalet }}</span>
                </div>
              </f7-list-item>
            </f7-list>
          </template>
        </UCAcordeon>
      </div>
    </div>
  </f7-page>
</template>

<script setup>
// @ts-check
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppOrdersStore } from '@/js/stores/appOrders';
import { useAppConfigStore } from '@/js/stores/appConfig';
import { useI18n } from 'vue-i18n';
import { logError } from '@/js/utils/log';
import { HiNotification } from '@/js/common/HINotificationSupport';
import { callFetchCommonPackagingSalesOrders } from '@/js/services/APIErp';

import TheNavbar from '@/components/TheNavbar.vue';
import UCAcordeon from '@/components/common/UCAcordeon.vue';
import UCPreload from '@/components/common/UCPreload.vue';

/** @import { Ref } from 'vue' */
/** @import { ConfeccionHabitualPedidos } from '@erpagroapp/types' */

const props = defineProps({
  clientId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits([
  'selectedPackaging',
]);

const { t } = useI18n();

const {
  actualOrder,
} = storeToRefs(useAppOrdersStore());

const {
  userEnviroment,
} = storeToRefs(useAppConfigStore());

const isLoadingPackaging = ref(false);
/** @type {Ref<ConfeccionHabitualPedidos[]>} */
const commonPackagingList = ref([]);
/** @type {Ref<{ Estado: string; items: ConfeccionHabitualPedidos[] }[]>} */
const commonPackagingFilter = ref([]);
const textFilter = ref('');
/** @type {Ref<InstanceType<typeof HTMLInputElement> | null>} */
const inputSearchRef = ref(null);
/** @type {Ref<ReturnType<typeof setTimeout> | null>} */
const timeout = ref(null);

/**
 * Gets the number of packages per pallet.
 * @param {ConfeccionHabitualPedidos} item - The packaging.
 * @returns {number} The number of packages per pallet.
 */
function getPackagesPerPallet(item) {
  const components = item.Producto?.Composicion?.Componentes;

  if (components) {
    for (const element of components) {
      if (element.Componente?.TipoArticulo === '1') {
        return element.Cantidad;
      }
    }
  }

  return item.EnvasesPorPalet;
}

/**
 * Fetches data from the server.
 * @param {number} clientId - The ID of the client.
 * @returns {Promise<void>} A promise that resolves when the data is fetched.
 */
async function fetchData(clientId) {
  try {
    const companyId = userEnviroment.value?.EntornoDeTrabajo.Empresa?.Id ?? 0;
    isLoadingPackaging.value = true;
    const { data } = await callFetchCommonPackagingSalesOrders(
      clientId,
      companyId,
    );
    if (Array.isArray(data) && data.length > 0) {
      /** @type {ConfeccionHabitualPedidos[]} */ (data).forEach((element) => {
        element.EnvasesPorPalet = getPackagesPerPallet(element);
      });
      commonPackagingList.value = data;
    } else {
      HiNotification.Toast(t('commercialization.info.noFrequentAssembliesFound'));
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast(t('commercialization.info.assemblyLoadingError'));
  } finally {
    isLoadingPackaging.value = false;
  }
}

/**
 * Emits the selected packaging.
 * @param {ConfeccionHabitualPedidos} packaging - The packaging.
 * @returns {void}
 */
function emitSelectedPackaging(packaging) {
  emit('selectedPackaging', packaging);
}

/**
 * Filters the main data.
 * @param {ConfeccionHabitualPedidos[]} listPackaging - The list of packaging.
 * @returns {Promise<void>}
 */
async function filterMainData(listPackaging) {
  // TODO: Consider creating a temp array, so the ref it not triggered thrice
  commonPackagingFilter.value = [];

  const addressSelected = {
    Estado: 'Dirección actual',
    items: listPackaging.filter((pack) => pack.Direccion?.Id === actualOrder.value?.DireccionEnvio?.Id),
  };

  const otherAddress = {
    Estado: 'Otras direcciones',
    items: listPackaging.filter((pack) => pack.Direccion?.Id !== actualOrder.value?.DireccionEnvio?.Id),
  };

  if (addressSelected.items.length > 0) {
    commonPackagingFilter.value.push(addressSelected);
  }

  if (otherAddress.items.length > 0) {
    commonPackagingFilter.value.push(otherAddress);
  }
}

/**
 * Handles the search input.
 * @param {Event} event - The event object.
 */
function handleSearchInput(event) {
  const { value } = /** @type {HTMLInputElement} */ (event.target);

  if (timeout.value) {
    clearTimeout(timeout.value);
  }

  timeout.value = setTimeout(() => {
    changeSearch(value);
  }, 650);
}

/**
 * Handles the change of the search input.
 * @param {string} value - The value of the input.
 * @returns {void}
 */
function changeSearch(value) {
  if (value !== '') {
    textFilter.value = value;

    /** @type {ConfeccionHabitualPedidos[]} */
    const filterElements = [];

    commonPackagingList.value.forEach((packaging) => {
      if (packaging.Producto?.Nombre?.toUpperCase().includes(value.toUpperCase())) {
        filterElements.push(packaging);
      }
    });

    filterMainData(filterElements);
  } else {
    if (inputSearchRef.value) {
      inputSearchRef.value.value = '';
    }
    textFilter.value = '';
    filterMainData(commonPackagingList.value);
  }
}

/**
 * Handles the loading of the data.
 * @param {number} clientId - The ID of the client.
 * @returns {Promise<void>} A promise that resolves when the data is loaded.
 */
async function handleLoading(clientId) {
  await fetchData(clientId);

  if (commonPackagingList.value.length > 0) {
    filterMainData(commonPackagingList.value);
  }
}

onMounted(() => {
  if (props.clientId) {
    handleLoading(props.clientId);
  }
});
</script>

<style lang="scss">
.frequent-sales-orders-packaging {
  .content-accordeon-component {
    margin-top: 50px;

    .storybook-action-sorted-accordion {
      // height: calc(100vh - 45px - var(--f7-page-navbar-offset) - var(--f7-page-navbar-offset) - 45px - 50px);
      .accordion-list {
        .accordion-item-opened {
          border: none;

          .media-list {
            border-bottom: 1px solid  var(--his-slate-blue);
          }

          .item-inner {
            .item-link {
              &::after {
                border-top: 0;
              }
            }
          }
        }
      }

      .container-info {
        color: #3e5155;
        font-weight: 500;
        font-size: 16px;

        &__text {
          display: flex;

          .title {
            color: var(--his-slate-blue);
            font-weight: 500;
            font-size: 16px;
            line-height: 16px;
            letter-spacing: -0.07px;
            margin-right: 15px;
            min-width: 122px;
          }

          .info {
            font-size: 14px;
            line-height: 19px;
          }
        }
      }
    }
  }

  .container-search {
    position: fixed;
    top: 50px;
    z-index: 1000;
    width: 100%;
    background: #fff;

    .container {
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      flex-wrap: nowrap;
      margin-top: 8px;
      height: 47px;

      &__filter-input {
        height: 47px;
        width: 100%;
        margin-right: 0;
        padding-left: 16px;
      }

      &__img {
        height: 47px;
        width: 47px;
        display: flex;
        flex-direction: column;
        align-content: center;
        justify-content: center;
      }

      i {
        margin-left: 25%;
      }
    }
  }
}
</style>
